/**
 * Sign out — the one helper every "Sign out" control uses (the top-bar menu in AppShell and the
 * "Choose a new password" gate in screens/ChangePassword).
 *
 * It only leaves the app once the server has ended the session, or says there was none (401).
 * Any other failure (offline, 5xx) keeps the person where they are and toasts SIGN_OUT_FAILED:
 * clearing locally while the ops_sid cookie is still valid would let /sign-in re-read /me and put
 * them straight back in, believing they had signed out (finding sf-c7de7891, t-3676686e).
 */
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ApiFailure, call } from '../api';
import { useToast } from '../ui/kit';
import { SIGN_OUT_FAILED } from './copy';

export interface SignOutDeps {
  /** POST /auth/logout. */
  logout: () => Promise<unknown>;
  /** Drop every cached query (qc.clear()) — only once the session is gone. */
  clear: () => void;
  /** Go to /sign-in. */
  leave: () => void;
  /** Tell the person it failed. */
  toast: (message: string) => void;
}

/** True when the session ended (or there was none); false when it failed and nothing was cleared. */
export async function runSignOut({ logout, clear, leave, toast }: SignOutDeps): Promise<boolean> {
  try {
    await logout();
  } catch (x) {
    if (!(x instanceof ApiFailure && x.status === 401)) {
      toast(SIGN_OUT_FAILED);
      return false;
    }
  }
  clear();
  leave();
  return true;
}

/** `signOut` for a button's onClick; `signingOut` while the request is in flight (a second press is ignored). */
export function useSignOut() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const toast = useToast();
  const [signingOut, setSigningOut] = useState(false);
  const inFlight = useRef(false);

  const signOut = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSigningOut(true);
    const ok = await runSignOut({
      logout: () => call('auth.logout'),
      clear: () => qc.clear(),
      leave: () => nav('/sign-in', { replace: true }),
      toast,
    });
    // On success this component is about to unmount; only a failure leaves it on screen to re-enable.
    if (!ok) {
      inFlight.current = false;
      setSigningOut(false);
    }
  }, [qc, nav, toast]);

  return { signOut, signingOut };
}
